import React, { createContext, FC, useMemo, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import { noop } from 'lodash';

import { AddToProjectModal } from '@app/components';
import { useQueryParams } from '@app/hooks';

interface ModalState {
  isOpen: boolean;
  id: string | null;
  redirectBackRoute?: string;
}

type ModalAction =
  | {
      type: 'OPEN_MODAL';
      payload: { id: string; redirectBackRoute?: string };
    }
  | { type: 'CLOSE_MODAL' };

interface ModalProviderProps {
  initialStateOverride?: Partial<ModalState>;
}

const initialState: ModalState = {
  isOpen: false,
  id: null,
};

const reducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        ...state,
        isOpen: true,
        id: action.payload.id,
        redirectBackRoute: action.payload.redirectBackRoute,
      };
    case 'CLOSE_MODAL':
      return initialState;
    default:
      return state;
  }
};

export const AddToProjectModalContext = createContext<{
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}>({
  state: initialState,
  dispatch: noop,
});

export const AddToProjectModalProvider: FC<ModalProviderProps> = ({
  initialStateOverride = {},
  children,
}) => {
  const { isAddToProject: shouldBeInitiallyOpen } = useQueryParams();
  const { id = '' } = useParams();

  const [state, dispatch] = useReducer(reducer, {
    ...(shouldBeInitiallyOpen
      ? {
          isOpen: true,
          id,
        }
      : initialState),
    ...initialStateOverride,
  });

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <AddToProjectModalContext.Provider value={value}>
      {children}
      <AddToProjectModal />
    </AddToProjectModalContext.Provider>
  );
};
